import { el } from '../utils/dom.js';

/**
 * PackTilesetDialog — Combines multiple individual tile images into a single
 * grid-aligned tileset spritesheet.
 *
 * Flow:
 *   1. User selects multiple PNG/WebP files (from a folder of individual tiles)
 *   2. Dialog shows count, auto-detected tile size, preview
 *   3. User adjusts tile size if needed, clicks "Create Tileset"
 *   4. Images are packed into a grid and loaded as a tileset
 */
export class PackTilesetDialog {
  constructor(state, compositor) {
    this.state = state;
    this.compositor = compositor;
  }

  /**
   * Show the file picker then the pack dialog.
   * @param {function} [onSuccess] — called with tileset name
   */
  pickAndShow(onSuccess) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.webp,.jpg,.jpeg';
    input.multiple = true;
    // webkitdirectory lets users pick a whole folder
    input.setAttribute('webkitdirectory', '');
    input.addEventListener('change', () => {
      const allFiles = [...input.files];
      const imageFiles = allFiles.filter(f => /\.(png|webp|jpe?g)$/i.test(f.name));
      if (imageFiles.length === 0) {
        alert(`No image files found in the selected folder (${allFiles.length} files scanned). Supported formats: PNG, WebP, JPG.`);
        return;
      }
      this._show(imageFiles, onSuccess);
    });
    input.click();
  }

  async _show(files, onSuccess) {
    const overlay = el('div', { className: 'dialog-overlay' });
    const dialog = el('div', { className: 'dialog', style: { minWidth: '480px', maxWidth: '560px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Pack Tileset from Images'));

    // Show dialog immediately with loading progress
    const loadingMsg = el('div', { className: 'psd-import-progress-text' },
      `Loading 0 / ${files.length} images...`);
    const loadingBar = el('div', { className: 'psd-import-progress-bar' });
    const loadingFill = el('div', { className: 'psd-import-progress-fill' });
    loadingBar.appendChild(loadingFill);
    dialog.appendChild(loadingBar);
    dialog.appendChild(loadingMsg);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Load all images with progress updates
    const images = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const img = await this._loadImage(files[i]);
        images.push({ name: files[i].name, img, width: img.width, height: img.height });
      } catch {
        // Skip files that fail to load
      }
      if (i % 10 === 0 || i === files.length - 1) {
        const pct = Math.round(((i + 1) / files.length) * 100);
        loadingMsg.textContent = `Loading ${i + 1} / ${files.length} images...`;
        loadingFill.style.width = `${pct}%`;
        // Yield to browser so the UI actually updates
        await new Promise(r => setTimeout(r, 0));
      }
    }

    if (images.length === 0) {
      loadingMsg.textContent = 'No valid images could be loaded from the selected files.';
      loadingBar.remove();
      dialog.appendChild(el('div', { className: 'dialog-buttons' },
        el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Close')));
      return;
    }

    // Remove loading UI
    loadingBar.remove();
    loadingMsg.remove();

    // Detect tile size from the most common dimensions
    const detected = this._detectSize(images);
    loadingMsg.remove();

    // Info
    const info = el('div', { className: 'psd-import-info' });
    info.appendChild(el('p', {},
      `Found ${images.length} images. They will be packed into a single tileset grid.`));

    // Folder name from path
    const folderName = files[0].webkitRelativePath
      ? files[0].webkitRelativePath.split('/')[0]
      : 'Packed Tileset';

    const details = el('div', { className: 'psd-import-details' });
    details.appendChild(el('div', { className: 'psd-import-detail' },
      el('strong', {}, 'Source: '), folderName));
    details.appendChild(el('div', { className: 'psd-import-detail' },
      el('strong', {}, 'Images: '), `${images.length} files`));
    details.appendChild(el('div', { className: 'psd-import-detail' },
      el('strong', {}, 'Detected tile size: '), `${detected.width} \u00D7 ${detected.height}`));
    info.appendChild(details);
    dialog.appendChild(info);

    // Tile size controls
    const sizeGroup = el('div', { className: 'form-group' });
    sizeGroup.appendChild(el('label', { className: 'form-label' }, 'Tile Size'));
    const sizeRow = el('div', { className: 'form-row' });
    const widthInput = el('input', {
      type: 'number', className: 'form-input', value: String(detected.width),
      min: '8', max: '512', style: { width: '80px' },
    });
    const heightInput = el('input', {
      type: 'number', className: 'form-input', value: String(detected.height),
      min: '8', max: '512', style: { width: '80px' },
    });
    sizeRow.append(widthInput, el('span', { style: { padding: '0 4px', color: 'var(--text-muted)' } }, '\u00D7'), heightInput);
    sizeGroup.appendChild(sizeRow);
    dialog.appendChild(sizeGroup);

    // Columns control
    const colGroup = el('div', { className: 'form-group' });
    colGroup.appendChild(el('label', { className: 'form-label' }, 'Columns'));
    const colInput = el('input', {
      type: 'number', className: 'form-input', value: '16',
      min: '1', max: '64', style: { width: '80px' },
    });
    colGroup.appendChild(colInput);
    dialog.appendChild(colGroup);

    // Name
    const nameGroup = el('div', { className: 'form-group' });
    nameGroup.appendChild(el('label', { className: 'form-label' }, 'Tileset Name'));
    const nameInput = el('input', {
      type: 'text', className: 'form-input', value: folderName,
    });
    nameGroup.appendChild(nameInput);
    dialog.appendChild(nameGroup);

    // Preview area
    const previewArea = el('div', { className: 'psd-import-preview' });
    dialog.appendChild(previewArea);

    // Error area
    const errorArea = el('div', { className: 'psd-import-error' });
    dialog.appendChild(errorArea);

    // Build preview on any setting change
    const updatePreview = () => {
      const tw = parseInt(widthInput.value, 10) || detected.width;
      const th = parseInt(heightInput.value, 10) || detected.height;
      const cols = parseInt(colInput.value, 10) || 16;
      this._renderPreview(previewArea, images, tw, th, cols);
    };
    widthInput.addEventListener('change', updatePreview);
    heightInput.addEventListener('change', updatePreview);
    colInput.addEventListener('change', updatePreview);
    updatePreview();

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const createBtn = el('button', { className: 'btn btn-primary', onClick: async () => {
      const tw = parseInt(widthInput.value, 10) || detected.width;
      const th = parseInt(heightInput.value, 10) || detected.height;
      const cols = parseInt(colInput.value, 10) || 16;
      const name = nameInput.value.trim() || folderName;

      createBtn.disabled = true;
      createBtn.textContent = 'Creating...';
      try {
        await this._createTileset(images, tw, th, cols, name, overlay, onSuccess);
      } catch (err) {
        errorArea.textContent = err.message || 'Failed to create tileset';
        createBtn.disabled = false;
        createBtn.textContent = 'Create Tileset';
      }
    }}, 'Create Tileset');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(createBtn, cancelBtn);
    dialog.appendChild(btnRow);
  }

  _loadImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  _detectSize(images) {
    const widthCounts = new Map();
    const heightCounts = new Map();
    for (const { width, height } of images) {
      widthCounts.set(width, (widthCounts.get(width) || 0) + 1);
      heightCounts.set(height, (heightCounts.get(height) || 0) + 1);
    }
    let bestW = 32, bestWC = 0;
    for (const [w, c] of widthCounts) if (c > bestWC) { bestW = w; bestWC = c; }
    let bestH = 32, bestHC = 0;
    for (const [h, c] of heightCounts) if (c > bestHC) { bestH = h; bestHC = c; }
    return { width: bestW, height: bestH };
  }

  _renderPreview(container, images, tw, th, cols) {
    container.innerHTML = '';
    const rows = Math.ceil(images.length / cols);
    const canvas = el('canvas', {
      className: 'psd-import-preview-canvas',
      style: { imageRendering: 'pixelated' },
    });

    const gridW = cols * tw;
    const gridH = rows * th;
    const maxPreviewWidth = 440;
    const scale = Math.min(maxPreviewWidth / gridW, 2, 200 / gridH || 2);
    canvas.width = Math.round(gridW * scale);
    canvas.height = Math.round(gridH * scale);

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    // Checkerboard
    for (let y = 0; y < canvas.height; y += 8) {
      for (let x = 0; x < canvas.width; x += 8) {
        ctx.fillStyle = (x + y) % 16 === 0 ? '#2a2a2a' : '#1a1a1a';
        ctx.fillRect(x, y, 8, 8);
      }
    }

    // Draw tiles
    for (let i = 0; i < images.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const { img, width, height } = images[i];

      // Center the image in the tile cell
      const fitScale = Math.min(tw / width, th / height, 1);
      const sw = width * fitScale;
      const sh = height * fitScale;
      const ox = (tw - sw) / 2;
      const oy = (th - sh) / 2;

      ctx.drawImage(img,
        0, 0, width, height,
        (col * tw + ox) * scale, (row * th + oy) * scale,
        sw * scale, sh * scale);
    }

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let c = 0; c <= cols; c++) {
      ctx.beginPath();
      ctx.moveTo(c * tw * scale, 0);
      ctx.lineTo(c * tw * scale, canvas.height);
      ctx.stroke();
    }
    for (let r = 0; r <= rows; r++) {
      ctx.beginPath();
      ctx.moveTo(0, r * th * scale);
      ctx.lineTo(canvas.width, r * th * scale);
      ctx.stroke();
    }

    container.appendChild(canvas);
    container.appendChild(el('div', { style: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' } },
      `${cols} \u00D7 ${rows} grid \u2014 ${images.length} tiles`));
  }

  async _createTileset(images, tw, th, cols, name, overlay, onSuccess) {
    const rows = Math.ceil(images.length / cols);
    const canvas = document.createElement('canvas');
    canvas.width = cols * tw;
    canvas.height = rows * th;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    for (let i = 0; i < images.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const { img, width, height } = images[i];

      const fitScale = Math.min(tw / width, th / height, 1);
      const sw = width * fitScale;
      const sh = height * fitScale;
      const ox = (tw - sw) / 2;
      const oy = (th - sh) / 2;

      ctx.drawImage(img, 0, 0, width, height,
        col * tw + ox, row * th + oy, sw, sh);
    }

    // Show progress while converting
    const progressOverlay = el('div', {
      style: {
        position: 'fixed', top: '0', left: '0', right: '0', bottom: '0',
        background: 'rgba(0,0,0,0.5)', zIndex: '10000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px', color: '#fff', fontSize: '14px',
      },
    }, `Packing ${images.length} tiles into tileset...`);
    document.body.appendChild(progressOverlay);

    // Yield so the overlay renders before the heavy canvas work
    await new Promise(r => setTimeout(r, 50));

    // Convert to Image
    const img = new Image();
    img.onload = () => {
      const tilesets = this.state.get('tilesets');
      const imageKey = `packed:${name}`;
      tilesets.push({
        name,
        image: imageKey,
        tileWidth: tw,
        tileHeight: th,
        columns: cols,
        tileCount: images.length,
        firstGid: tilesets.reduce((max, ts) => Math.max(max, (ts.firstGid || 0) + (ts.tileCount || 0)), 1),
        imageWidth: canvas.width,
        imageHeight: canvas.height,
        margin: 0,
        spacing: 0,
      });
      this.state.set('tilesets', tilesets);

      const tilesetImages = this.state.get('tilesetImages');
      tilesetImages.set(imageKey, img);
      this.state._notify('tilesets', tilesets, tilesets);
      this.state._notify('tilesetImages', tilesetImages, tilesetImages);

      this.compositor.requestRender();
      progressOverlay.remove();
      overlay.remove();
      if (onSuccess) onSuccess(name);
    };
    img.onerror = () => {
      progressOverlay.remove();
      alert('Failed to create tileset image. The tileset may be too large.');
    };
    img.src = canvas.toDataURL('image/png');
  }
}
